<template>
  <div class="container agency">
    <div class="agency__title-container">
      <div class="agency__title-left">
        <div class="agency__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.prevent.stop="cancelSortingAndFilters"
            class="agency__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="agency__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="agency__title-right"
           v-if="isDataFetched">
        <div class="export-container">
          <div
            v-tooltip.bottom-start="{content: 'Export CSV'}"
            class=""
            :class="[]"
            @click="exportCsv"
          >
            <base-material-design-icon
              icon="download"
              class=""
            />
          </div>
        </div>
        <div class="agency__agencies">
          <span class="agency__count">Agencies: {{totalItems}}</span>
          <base-select
            class="agency__grid-select"
            alignLeft
            showTopBorder
            shiftLabelRight
            :options="paginationOptions"
            :titleStyle="{'padding-left': '5px'}"
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
      </div>
    </div>

    <div class="common-table agency__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in agencyTableColumnsConfig"
             :key="column.property"
             :class="{[`agency__${column.property}`]: true}">
          <base-multi-select-sortable-header
            v-if="column.property !== 'close_button'"
            class="agency__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedNumberFilter="['clients_count'].includes(column.property) ? filterParams[column.property] : null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="agency__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <div :key="agency.id" v-for="agency in paginatedAgencies" class="common-table__row">
          <div class="common-table__cell"
                v-for="column in agencyTableColumnsConfig"
                :key="column.property"
                :class="[`agency__${column.property}`, `agency__row_${column.property}`]">
            <template v-if="['is_active'].includes(column.property)">
                <base-select class="agency__select"
                              :options="column.options"
                              borderLess
                              alignLeft
                              shiftLabelRight
                              :selectStyle="{height: '60px!important', width: '75px', marginLeft: '-10px'}"
                              v-model="agency[column.property]"
                              @change="createOrUpdateAgency(agency, false, true)"/>
            </template>
            <template v-else-if="['name', 'email'].includes(column.property)">
                <input
                    :class="[
                      'agency__input',
                      {
                        'agency__input_err': errorFields[agency.id] && errorFields[agency.id][column.property]
                      }
                    ]"
                    v-model="agency[column.property]"
                    type="text"
                    @focus="removeFieldFromErrors(agency.id, column.property)"
                    @blur="createOrUpdateAgency(agency)"
                    @keyup.enter="($event) => { $event.target.blur() }"/>
            </template>
            <template v-else-if="['content_brief_count', 'keyword_count'].includes(column.property)">
              <span>{{ agency[column.property]?.toLocaleString() }}</span>
            </template>
            <template v-else-if="['plan_type'].includes(column.property)">
              <span>{{ planTypes[agency[column.property]] }}</span>
            </template>
            <template v-else-if="['clients_count'].includes(column.property)">
              <a :href="getClientsLink(agency.id)" target="_blank">{{ agency[column.property] }}</a>
            </template>
            <template v-else-if="['stripe_account_standing'].includes(column.property)">
              <span>{{ agency[column.property] ? 'Active' : 'Inactive' }}</span>
            </template>
            <template v-else-if="['stripe_product'].includes(column.property)">
              <a :href="getStripeLink(agency.stripe_customer_id)" target="_blank">{{ stripeProducts.find(product => product.id === agency[column.property])?.name }}</a>
            </template>
            <template v-else-if="column.property === 'delete_button'">
              <span>{{ agency[column.property] }}</span>
              <base-material-design-icon @click="showConfirmModal(agency.id)"
                                          icon="close"/>
            </template>
          </div>
        </div>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="agency__update-loader" v-if="showUpdateLoader">
      <base-loader class="agency__update-loader-ring"/>
    </div>
    <modal class="agency__confirm-modal" name="confirm-modal" v-bind="confirmModalProps">
      <base-confirm-modal title="Are you sure you want to delete this?"
                          subtitle="This Agency will be removed permanently!"
                          @confirm="confirmModal" />
    </modal>
    <modal name="create" v-bind="changeAgencyModalProps">
      <CreateAgencyPopup
        ref="create-agency"
        @apply="createOrUpdateAgency($event, true, true)"
        @cancel="toogleCreateModal(false)"/>
    </modal>
  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateAgencyPopup from './CreateAgencyPopup'
import _ from 'lodash'
import moment from 'moment'
import ToasterService from '@/services/toasterService'
import BaseSortMixins from '@/common/BaseSortMixins'
import csvExport from '@/services/CSVExport'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  name: '',
  email: '',
  is_active: [],
  clients_count: {
    operator: null,
    value: null
  },
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const ACTIVE_OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
export default {
  name: 'Agency',
  components: { CreateAgencyPopup },
  mixins: [BaseSortMixins],
  // filters received form url
  props: ['filters'],
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      selectedAgency: null,
      removeAgencyId: -1,
      sortConfig: [],
      errorFields: {},
      pageItemsLimit: ITEMS_PER_PAGE,
      // flag notifies that filters received form url query was applied
      isQueryFiltersApplied: false,
      activeOptions: _.cloneDeep(ACTIVE_OPTIONS),
      // agencyTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'email': 'email',
        'is_active': 'is_active',
        'clients_count': 'clients_count'
      },
      paginationOptions: [...PAGINATION_OPTIONS]
    }
  },
  watch: {
    clientId: {
      handler (v) {
        if (v) {
          this.filterParams = _.cloneDeep(EMPTY_FILTERS)
          this.loadData()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('agency', ['agencies']),
    ...mapState(['userGroups', 'stripeProducts']),
    agencyTableColumnsConfig () {
      return [
        {
          label: 'Agency',
          property: 'name',
          isDefaultSort: true // this column will be sorted by default
        },
        {
          label: 'Email',
          property: 'email'
        },
        // used 2 different active option because BaseMultiSelectSortableHeader don't understand 0 value
        {
          label: 'Is Active',
          property: 'is_active',
          isSmartInput: false,
          options: [
            {
              label: 'Yes',
              value: 1
            },
            {
              label: 'No',
              value: 0
            }
          ]
        },
        {
          label: 'Plan Type',
          property: 'plan_type',
          isSmartInput: false,
          options: this.userGroups['user_plan_group'].map(group => ({ label: group.name, value: group.id }))
        },
        {
          label: 'Stripe Product',
          property: 'stripe_product',
          isSmartInput: false,
          options: this.stripeProducts.map(product => ({ label: product.name, value: product.id }))
        },
        {
          label: 'Billing Status',
          property: 'stripe_account_standing',
          isSmartInput: false,
          options: [{ label: 'Inactive', value: 0 }, { label: 'Active', value: 1 }]
        },
        {
          label: 'Projects',
          property: 'clients_count',
          isNumber: true,
          isSmartInput: false
        },
        {
          label: 'Keywords',
          property: 'keyword_count',
          isNumber: true,
          isSmartInput: false
        },
        {
          label: 'Briefs',
          property: 'content_brief_count',
          isNumber: true,
          isSmartInput: false
        },
        {
          label: '',
          property: 'delete_button',
          isSmartInput: false
        }
      ]
    },
    planTypes () {
        return this.userGroups['user_plan_group'].reduce((prev, cur) => {
          prev[cur.id] = cur.name
          return prev
        }, {})
    },
    paginatedAgencies (state) {
      this.errorFields = {}
      let agencies = _.cloneDeep(this.agencies)
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100, name, email, is_active, clients_count } = this.filterParams
      is_active.includes(-1) && is_active.push(0)
      agencies = agencies.filter(agency => {
        const nameFilter = !name || agency.name.toLowerCase().includes(name.toLowerCase())
        const emailFilter = !email || agency.email.toLowerCase().includes(email.toLowerCase())
        const isActiveFilter = !is_active.length || is_active.includes(agency.is_active)
        const clientsCount = (
          (!clients_count.operator) ||
          (clients_count.operator === 'equal' && +clients_count.value === agency.clients_count) ||
          (clients_count.operator === 'ne' && +clients_count.value !== agency.clients_count) ||
          (clients_count.operator === 'gt' && +clients_count.value < agency.clients_count) ||
          (clients_count.operator === 'lt' && +clients_count.value > agency.clients_count) ||
          (clients_count.operator === 'lte' && +clients_count.value >= agency.clients_count) ||
          (clients_count.operator === 'gte' && +clients_count.value <= agency.clients_count)
        )

        return nameFilter && emailFilter && isActiveFilter && clientsCount
      })
      return helper.sortByMultipleParams(agencies, this.sortConfig).slice(page_from, page_to)
    },
    totalItems () {
      return this.paginatedAgencies.length
    },
    confirmModalProps () {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    },
    dataMissing () {
      return this.isDataFetched && !this.totalItems
    },
    changeAgencyModalProps () {
      const changeAgencyModalProps = {
        width: 750
      }
      return { ...baseModalProps, ...changeAgencyModalProps }
    },
    hasPagination () {
      return this.isDataFetched && !this.isDataUpdating && this.totalItems > this.pageItemsLimit
    },
    showUpdateLoader () {
      return this.isDataFetched && this.isDataUpdating
    }
  },
  methods: {
    ...mapActions('agency', ['getAgencyData', 'createUpdateAgency', 'removeAgency']),
    exportCsv () {
      const header = this.agencyTableColumnsConfig.map(col => col.label)
      header.pop()
      const data = []
      this.agencies.forEach(agency => {
        const row = []
        this.agencyTableColumnsConfig.forEach((header, index, array) => {
          if (index === array.length - 1) {
            return
          }
          let value = agency[header.property]
          if (header.property === 'plan_type') {
            value = this.planTypes[value]
          } else if (header.property === 'stripe_account_standing') {
            value = value ? 'Active' : 'Inactive'
          } else if (header.property === 'stripe_product') {
            value = this.stripeProducts.find(product => product.id === agency[header.property])?.name
          }
          value = (value ?? '').toString()
          row.push(value)
        })
        data.push(row)
      })
      csvExport(`Agencies_Export_${moment().format('YYYYMMDDHHmm')}`, [header, ...data])
    },
    async loadData () {
      this.isDataUpdating = true
      await this.getAgencyData()
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    reset () {
      this.sortConfig = []
      this.agencyTableColumnsConfig = _.cloneDeep(DEFAULT_CONFIG)
      this.setupFilterParams()
      this.loadData()
    },
    setupFilterParams () {
      // this.filters - filters received form url query as props
      if (!this.filters || this.isQueryFiltersApplied) {
        this.filterParams = { ...this.filterParams, ..._.cloneDeep(EMPTY_FILTERS) }
        return true
      }
      const filters = JSON.parse(this.filters)
      this.filterParams = _.cloneDeep({ ...this.filterParams, ...EMPTY_FILTERS, ...filters })
      if (filters.sort_column && filters.sort_column.length > 0) {
        filters.sort_column.forEach((columnName, index) => {
          const propertyName = columnName
          const sortDirection = filters.sort_order[index]
          const valuePreFormatter = null
          this.sortConfig.push({ propertyName, sortDirection, valuePreFormatter })
        })
      }
      this.isQueryFiltersApplied = true
      return true
    },
    getStripeLink (id) {
      return `https://dashboard.stripe.com/customers/${id}`
    },
    getClientsLink (id) {
      return `${document.location.origin}/dashboard#/system/clients?agency=${id}`
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    removeFieldFromErrors (id, field) {
      if (this.errorFields[id] && this.errorFields[id][field]) {
        delete this.errorFields[id][field]
      }
    },
    async createOrUpdateAgency (agency, isModal = false, isNeedUpdate = false) {
      isModal && (this.isDataUpdating = true)
      const response = await this.createUpdateAgency({
        method: agency.id > 0 ? 'patch' : 'post',
        ...agency
      })
      if (!isModal) {
        this.errorFields = {
          ...this.errorFields,
          [agency.id]: response.error_fields || {}
        }
      }
      if (response.is_successful) {
        const successMessage = agency.id > 0 ? 'Agency updated successfully' : 'Agency created successfully'
        ToasterService.showToast(ToasterService.SUCCESS_TYPE, successMessage)
      }
      if (isModal && response.error_fields && Object.keys(response.error_fields).length) {
        this.$refs['create-agency'].externalErrors = response.error_fields
      } else if (isModal && response.error_fields && !Object.keys(response.error_fields).length) {
        this.toogleCreateModal(false)
      }
      if (isNeedUpdate && response.error_fields && !Object.keys(response.error_fields).length) {
        await this.loadData()
      }
      this.isDataUpdating = false
    },
    showConfirmModal (id) {
      this.removeAgencyId = id
      this.$modal.show('confirm-modal')
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.deleteAgency()
      }
    },
    async deleteAgency () {
      this.isDataUpdating = true
      await this.removeAgency({ id: this.removeAgencyId })
      await this.loadData()
      this.toogleCreateModal(false)
      this.isDataUpdating = false
    },
    toogleCreateModal (open = true) {
      if (open) {
        this.$modal.show('create')
      } else {
        this.$modal.hide('create')
      }
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
    }
  }
}
</script>

<style scoped lang="scss">
.common-table {
  border-radius: 4px;
  overflow-x: scroll;
  width: 90vw;
  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    // padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
    // border: 1px solid red;
  }
  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    // padding: 0;
    display: flex;
    align-items: center;
    // font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;
    &:first-child {
      // padding-left: 25px;
    }
    // border: 1px solid red;
    padding: 0 5px;
  }
  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
    display: flex;
  }
}
  // .common-table__header-cell {
  //   flex: 0 0 25%;
  // }
.agency {
  position: relative;
  min-height: 200vh !important;
  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }
  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }
  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }
  &__title-left {
    display: flex;
    align-items: center;
  }
  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }
  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }
  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;
    &:hover {
      opacity: 0.5;
    }
  }
  &__clear-filters {
    @extend .agency__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;
    span:first-child {
      font-size: 11px;
    }
  }
  &__grid-select {
    flex-basis: 100px;
  }
  &__input {
    width: 100%;
    font-size: 14px;
    padding: 22px 0 21px 25px;
    border: none;
    &:hover {
      background-color: rgba(40, 74, 255, .1);
    }
    &:focus {
      outline: none;
      border-bottom: 2px solid $rfx-bright-blue;
      background-color: rgba(40, 74, 255, .1);
    }
    &_err {
      padding-top: 21px;
      border-top: none;
      border-bottom: 2px solid $rfx-light-red;
      color: $rfx-light-red;
    }
  }
  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }
  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }
  &__row-button-cross {
    cursor: pointer;
    font-size: 26px;
    position: absolute;
    right: 10px;
    transform: rotate(45deg);
  }
  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }
  &__update-loader-ring {
    margin-top: 200px;
  }
  &__confirm-modal {
    background-color: transparent !important;
  }
  &__agencies {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }
  &__email,
  &__name {
    // padding: 0 !important;
    flex-basis: 10%;
  }
  &__email {
    flex-basis: 15%;
    // &.common-table__cell {
    //   flex: 0 0 15%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 15%;
    // }

    // .agency__input {
    //   width: 90%;
    // }
  }
  &__delete_button {
    flex-basis: 2%;
    // &.common-table__cell {
    //   flex: 0 0 15%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 15%;
    // }
  }
  &__plan_type {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 15%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 15%;
    // }
  }
  &__stripe_account_standing {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__stripe_product {
    flex-basis: 15%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__keyword_count {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__content_brief_count {
    flex-basis: 8%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__last_billing_date {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__clients_count {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
    // position: relative;

    a {
      color: $rfx-bright-blue !important;
      text-decoration: none !important;
    }
  }
  &__name {
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;

    //   &::v-deep .multi-select__title-container {
    //     padding-left: 26px;
    //   }
    // }
  }
  &__is_active {
    flex-basis: 10%;
    // &.common-table__cell {
    //   flex: 0 0 10%;
    // }
    // &.common-table__header-cell {
    //   flex: 0 0 10%;
    // }
  }
  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }
  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }
}
.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: 4px;
}
.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}
.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}
.export-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 85%;
}
</style>
